import { EURO_SYMBOL } from '~/constants';

type PriceInfo = {
  special: number;
  regular: number;
};

type ProductItem = {
  sku: string;
  name: string;
  allPrice: PriceInfo;
  img: string;
  titleOfRecipe: string;
};

type TargetInfo = {
  id: string;
  nameOfProduct: string;
  productPrice: number;
  recipePrice: number;
  sku: string;
  titleOfRecipe: string;
};

type FormattedProductItem = {
  item_id: string;
  item_sku: string;
  item_name: string;
  discount: number;
  discount_in_cents: number;
  new_product: null;
  quantity: number;
  price: number;
  price_in_cents: number;
  currency: string;
  isSelected?: boolean;
};

const gtmPushEvent = (eventName: string, eventProperties: any) => {
  if (typeof dataLayer !== 'undefined' && typeof dataLayer.push === 'function') {
    dataLayer.push({ ecommerce: null });
    dataLayer.push(Object.assign({ event: eventName }, eventProperties));
  } else if (typeof window !== 'undefined') {
    console.error('dataLayer is not defined');
  }
};

// NOTE - we use "100" to convert back and forth between cents and euros, if we use a differents currencies, we need to change this

const gtmDiscountValue = (price, discountPrice, inCents = false, quantity = 1) => {
  if (!discountPrice) return '';
  const discountedPrice = inCents ? (price - discountPrice) * 100 : price - discountPrice;
  const totalDiscountedPrice = discountedPrice * quantity;
  return Number(totalDiscountedPrice.toFixed(2));
};

const gtmPriceFormat = (price: number, inCents: boolean = false) => {
  if (typeof price !== 'number') {
    return;
  }
  return inCents ? Number((price * 100).toFixed(2)) : Number(price.toFixed(2));
};

function cartDiscountPrice({ regular, quantity }, special) {
  return special === null ? null : (regular - special) / quantity;
}

const gtmValue = (price, discountPrice, inCents = false, quantity: number = 1) => {
  const value = discountPrice || price;
  return inCents ? Number((value * 100 * quantity).toFixed(2)) : Number((value * quantity).toFixed(2));
};

const formatDataForGtm = (data: string) => {
  return data.replace(/-/g, '_');
};

const centimesToEuros = (centimes) => {
  return Number((centimes / 100).toFixed(2));
};

const priceStringToNumber = (priceString) => {
  const noEuroSign = priceString.replace(EURO_SYMBOL, '');
  const dotInsteadOfComma = noEuroSign.replace(',', '.');
  return parseFloat(dotInsteadOfComma);
};

/**
 * Transforms an array of subarrays of product items based on provided targets and formats product data.
 * Each product is enhanced with additional properties including formatted prices, product status, and selection flag.
 *
 * @param {ProductItem[][]} data - An array of subarrays, where each subarray contains objects representing individual products.
 * @param {TargetInfo[]} targets - An array of objects, each specifying a titleOfRecipe and sku to be used for determining if the product matches the selection criteria.
 * @returns {FormattedProductItem[]} Returns a new array of subarrays
 */
function formatProductItemSurveyForGTMRecapeEnd(data: ProductItem[][], targets: TargetInfo[]) {
  return data.map((subArray) =>
    subArray.map(
      (item): FormattedProductItem => ({
        item_id: `SKU_${item.sku}`,
        item_sku: item.sku,
        item_name: item.name,
        discount: gtmPriceFormat(item.allPrice.special) || 0,
        discount_in_cents: gtmPriceFormat(item.allPrice.special, true) || 0,
        new_product: null,
        quantity: 1,
        price: item.allPrice.special || item.allPrice.regular,
        price_in_cents: gtmPriceFormat(item.allPrice.special || item.allPrice.regular, true),
        currency: 'EUR',
        isSelected: targets.some((target) => target.titleOfRecipe === item.titleOfRecipe && target.sku === item.sku),
      }),
    ),
  );
}
// legacy code for the skincare survey
/**
 * Transforms an array of subarrays of product items based on provided targets and formats product data.
 * Each product is enhanced with additional properties including formatted prices, product status, and selection flag.
 *
 * @param {ProductItem[][]} data - An array of subarrays, where each subarray contains objects representing individual products.
 * @param {TargetInfo[]} targets - An array of objects, each specifying a titleOfRecipe and sku to be used for determining if the product matches the selection criteria.
 * @returns {FormattedProductItem[][]} Returns a new array of subarrays
 */
function formatProductItemSurveyForGTMRecapeStartLegacy(data: ProductItem[][]) {
  return data.map((subArray) =>
    subArray.map(
      (item): FormattedProductItem => ({
        item_id: `SKU_${item.sku}`,
        item_sku: item.sku,
        item_name: item.name,
        discount: gtmPriceFormat(item.allPrice.special) || 0,
        discount_in_cents: gtmPriceFormat(item.allPrice.special, true) || 0,
        new_product: null,
        quantity: 1,
        price: item.allPrice.special || item.allPrice.regular,
        price_in_cents: gtmPriceFormat(item.allPrice.special || item.allPrice.regular, true),
        currency: 'EUR',
      }),
    ),
  );
}

/**
 * Transforms an array of product items based on provided schema and formats product data.
 * Each product is enhanced with additional properties including formatted prices, product status, and selection flag.
 *
 * @param {ProductItem[]} data - An array of objects, each representing an individual product.
 * @returns {FormattedProductItem[]} Returns a new array with formatted product data for GTM.
 */
function formatProductItemSurveyForGTMRecapeStart(data) {
  return data.map((item) => ({
    item_id: `SKU_${item.sku}`,
    item_sku: item.sku,
    item_name: item.name,
    discount: gtmPriceFormat(item.allPrice.special) || 0,
    discount_in_cents: gtmPriceFormat(item.allPrice.special, true) || 0,
    new_product: null,
    quantity: 1,
    price: item.allPrice.special ?? item.allPrice.regular,
    price_in_cents: gtmPriceFormat(item.allPrice.special ?? item.allPrice.regular, true),
    currency: 'EUR',
  }));
}
export {
  formatProductItemSurveyForGTMRecapeEnd,
  formatProductItemSurveyForGTMRecapeStartLegacy,
  formatProductItemSurveyForGTMRecapeStart,
  priceStringToNumber,
  gtmPushEvent,
  formatDataForGtm,
  centimesToEuros,
  gtmDiscountValue,
  gtmValue,
  gtmPriceFormat,
  cartDiscountPrice,
};
